/**
 * @fileoverview Hook de Actions Layer para operaciones de negocio de plantillas API y envío de mensajes WhatsApp.
 * Implementa el patrón de **Actions Layer** con separación estricta de responsabilidades, enfocado en operaciones de negocio para envío de mensajes y recordatorios usando plantillas API.
 * Incluye optimizaciones de performance, manejo robusto de errores y documentación completa de flujos y tipos de datos.
 * @module useApiTemplateActions
 * @category Hook - Actions Layer
 * @since 1.0.0
 * @version 3.1.0 - Optimización de performance, documentación JSDoc completa, separación de capas y manejo avanzado de errores
 */
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import useCompleteMessageActions from 'presentation/hooks/inbox/actions/useCompleteMessageActions';
import useSelectedConversation from 'presentation/hooks/shared/useSelectedConversation';
import { createReminder } from 'actions/whatsapp_karix';
import {
  resolveTemplateCustomerId,
  resolveTemplateChatId
} from 'services/inbox/resolveOutboundRecipient';
import { customerPhoneNumber } from 'services/inbox/resolveNewChatMsIntegration';
import { getCountryFlagEmoji } from 'services/countryFlag';

const useApiTemplateActions = (
  currentCustomer,
  selectedTemplate,
  t,
  dynamicFieldsData,
  msSendContext = {}
) => {
  const dispatch = useDispatch();
  const conversation = useSelectedConversation();

  // Obtener acciones de mensajería desde hook existente
  const {
    sendAndAddMessage,
    sendAndAddMessageWithFiles,
    sendAndAddMessageCarousel,
    sendMessageOnly,
    sendMessageWithFilesOnly
  } = useCompleteMessageActions();

  // Token CSRF para operaciones que lo requieren
  const csrfToken = document.querySelector('[name=csrf-token]')?.content;

  /**
   * Construye el objeto de parámetros dinámicos para una plantilla API.
   */
  const buildTemplateParams = useCallback((formData) => {
    const templateParams = {};

    // Procesar variables del template principal
    const mainVariables = dynamicFieldsData?.main?.variables || [];
    mainVariables.forEach((variable) => {
      const value = formData[variable.fieldName];
      if (value) {
        const field = dynamicFieldsData.main.fields[variable.fieldName];
        if (field?.id) {
          templateParams[field.id] = value;
        }
      }
    });

    // Procesar variables de carousels
    const carousels = dynamicFieldsData?.cards || [];
    carousels.forEach((carousel) => {
      // Body
      carousel?.body?.variables?.forEach((variable) => {
        const value = formData[variable.fieldName];
        if (value) {
          const field = carousel?.body?.fields[variable.fieldName];
          if (field?.id) {
            templateParams[field.id] = value;
          }
        }
      });
      // Botones
      carousel?.buttons?.forEach((button) => {
        button?.variables?.forEach((variable) => {
          const value = formData[variable.fieldName];
          if (value) {
            const field = button?.fields[variable.fieldName];
            if (field?.id) {
              templateParams[field.id] = value;
            }
          }
        });
      });
    });

    return templateParams;
  }, [dynamicFieldsData]);

  /**
   * Envía un mensaje usando una plantilla API de WhatsApp.
   */
  const sendTemplateMessage = useCallback(async (data, { onSuccess, onError } = {}) => {
    // Construir parámetros de template internamente
    const templateParams = buildTemplateParams(data);
    if (!selectedTemplate || !currentCustomer) {
      const error = t?.('errors.templateAndCustomerRequired');
      onError?.(error);
      return { success: false, error };
    }

    try {
      /** @type {MessageData} */
      const customerId = resolveTemplateCustomerId(currentCustomer, conversation);
      const chatId = msSendContext.chatId || resolveTemplateChatId(conversation, customerId);
      const integration = msSendContext.integration;
      if (msSendContext.isMsWhatsapp && !integration?.id) {
        const error = t?.('errors.integrationRequired');
        onError?.(error);
        return { success: false, error };
      }

      const resolvedMessage = data?.message || '';
      const messageData = {
        type: selectedTemplate.templateType,
        template: true,
        platform: msSendContext.isMsWhatsapp ? integration.platform : 'whatsapp',
        customer_id: customerId != null ? String(customerId) : customerId,
        message_identifier: uuidv4(),
        template_params: Object.values(templateParams) || []
      };
      if (chatId) {
        messageData.chat_id = String(chatId);
      }
      if (msSendContext.isMsWhatsapp) {
        messageData.template_id = String(selectedTemplate.gupshupTemplateId);
        if (!chatId) {
          messageData.platform_retailer_id = integration.id;
          messageData.create_chat = true;
          messageData.first_name = currentCustomer?.first_name;
          messageData.last_name = currentCustomer?.last_name;
          messageData.username = currentCustomer?.username;
          const phone = customerPhoneNumber(currentCustomer);
          if (phone) {
            messageData.phone_number = phone;
            const emojiFlag = getCountryFlagEmoji(currentCustomer?.country_id);
            if (emojiFlag) messageData.emoji_flag = emojiFlag;
          }
        }
      } else {
        messageData.gupshup_template_id = selectedTemplate.gupshupTemplateId;
      }

      if (selectedTemplate.templateType === "carousel") {
        const carouselFiles = [];
        const carousels = dynamicFieldsData?.cards || [];
        carousels.forEach((_, index) => {
          const carouselFile = data?.[`carouselFile_${index}`]?.[0];
          if (carouselFile) {
            carouselFiles.push(carouselFile);
          }
        });
        messageData.cards_files = carouselFiles;
        messageData.caption = resolvedMessage;
        if (chatId) {
          sendAndAddMessageCarousel(messageData, selectedTemplate);
        } else {
          sendMessageWithFilesOnly(messageData);
        }
        onSuccess?.();
        return { success: true };
      }

      // Configurar datos específicos según tipo de plantilla
      const hasMediaContent = ["document", "image", "video"].includes(selectedTemplate.templateType);

      if (hasMediaContent) {
        messageData.file_data = data?.attachedFile?.[0];
        messageData.caption = resolvedMessage;
        if (chatId) {
          sendAndAddMessageWithFiles(messageData);
        } else {
          sendMessageWithFilesOnly(messageData);
        }
      } else if (chatId) {
        messageData.message = resolvedMessage;
        sendAndAddMessage(messageData);
      } else {
        messageData.message = resolvedMessage;
        sendMessageOnly(messageData);
      }

      onSuccess?.();
      return { success: true };
    } catch (error) {
      const errorMessage = error.message || t?.('errors.errorSendingTemplate');
      onError?.(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [
    selectedTemplate,
    currentCustomer,
    sendAndAddMessage,
    sendAndAddMessageWithFiles,
    sendAndAddMessageCarousel,
    sendMessageOnly,
    sendMessageWithFilesOnly,
    t,
    buildTemplateParams,
    dynamicFieldsData,
    conversation,
    msSendContext
  ]);

  /**
   * Crea y envía un recordatorio programado usando una plantilla API de WhatsApp.
   */
  const sendReminderMessage = useCallback(async (data, { onSuccess, onError } = {}) => {
    // Construir parámetros de template internamente
    const templateParams = buildTemplateParams(data, dynamicFieldsData);
    if (!selectedTemplate || !currentCustomer || !data.sendDate) {
      const error = t?.('errors.templateCustomerDateRequired');
      onError?.(error);
      return { success: false, error };
    }

    try {
      const timezone = new Date().toString().match(/([-+][0-9]+)\s/)?.[1] || '+0000';
      const params = new FormData();

      const customerId = resolveTemplateCustomerId(currentCustomer, conversation);
      params.append('reminder[customer_id]', customerId);
      params.append('reminder[whatsapp_template_id]', selectedTemplate.id);
      params.append('reminder[send_at]', moment(data.sendDate).format('YYYY-MM-DD HH:mm:ss'));
      params.append('reminder[timezone]', timezone);
      if (data.message) {
        params.append('reminder[template_text]', data.message);
      }

      // Agregar archivo si es requerido por el tipo de plantilla
      if (["document", "image", "video", "carousel"].includes(selectedTemplate.templateType)) {
        if (selectedTemplate.templateType === "carousel") {
          // Para carousel agregar múltiples archivos de cada card
          const carousels = dynamicFieldsData?.cards || [];
          carousels.forEach((_, index) => {
            const carouselFile = data?.[`carouselFile_${index}`]?.[0];
            if (carouselFile) {
              params.append("reminder[cards_files][]", carouselFile);
            }
          });
        } else if (data.attachedFile?.[0]) {
          // Para otros tipos agregar solo el primer archivo
          params.append('reminder[file]', data.attachedFile[0]);
        }
      }

      // Agregar parámetros de contenido
      const insertedParams = Object.values(templateParams);
      insertedParams.forEach((value) => {
        params.append('reminder[content_params][]', value);
      });

      const platformRetailerId = selectedTemplate?.platformRetailerId || msSendContext.integration?.id;
      if (platformRetailerId) {
        params.append('reminder[platform_retailer_id]', platformRetailerId);
      }

      const chatId = msSendContext.chatId || resolveTemplateChatId(conversation, customerId);
      if (chatId) {
        params.append('reminder[chat_id]', chatId);
      }

      dispatch(createReminder(params, csrfToken));

      onSuccess?.();
      return { success: true };
    } catch (error) {
      const errorMessage = error.message || t?.('errors.errorCreatingReminder');
      onError?.(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [
    t,
    selectedTemplate,
    currentCustomer,
    dispatch,
    csrfToken,
    buildTemplateParams,
    dynamicFieldsData,
    conversation,
    msSendContext
  ]);

  /**
   * Retorna las funciones de Actions Layer optimizadas para operaciones con plantillas API.
   *
   * @returns {Object} { sendTemplateMessage, sendReminderMessage, buildTemplateParams }
   */
  return useMemo(() => ({
    sendTemplateMessage,
    sendReminderMessage,
    buildTemplateParams
  }), [sendTemplateMessage, sendReminderMessage, buildTemplateParams]);
};

export default useApiTemplateActions;
